import { useCallback, useMemo } from 'react';
import { Typography } from '@mui/material';
import { isEmpty, orderBy } from 'lodash';
import { PINNED_FIRMS_KEY } from 'common/constants/userPreferences';
import { useLocalStorageByUser } from 'common/hooks';
import FeatureEnabler from 'components/FeatureEnabler';
import AddFirmAction from '../AddFirmAction';
import ContactOrInquireLicensing from '../ContactAdminOrInquireLicensing';
import FirmOption from '../FirmOption';
import LogoutButton from '../LogoutBtn';
import { FirmListProps, LoginFirm } from './FirmList.props';

const FirmList = ({ firmList, firmListAuthorized, addFirmHandler, searchState, user }: FirmListProps) => {
  const [pinnedFirms, setPinnedFirms] = useLocalStorageByUser(PINNED_FIRMS_KEY, []);

  const updatePinStatus = useCallback((id: number, title: string, isPinned: boolean) => {
    let tmpPinnedOptions: any = {};

    if (isPinned) {
      tmpPinnedOptions = pinnedFirms.filter((o: any) => o.value !== id) ?? [];
    } else {
      tmpPinnedOptions = [...pinnedFirms, { value: id, label: title }];
    }

    setPinnedFirms(tmpPinnedOptions);
  }, [pinnedFirms, setPinnedFirms]);

  const processedFirmList = useMemo(() => {
    if (!firmList) {
      return [];
    }
    
    return firmList.map((firm: LoginFirm) => {
      const isFirmPinned = Boolean(
        pinnedFirms.find((pinnedFirm: any) => pinnedFirm.value === firm.id)
      );

      return {
        ...firm,
        isPinnable: true,
        isPinned: isFirmPinned,
        updatePinStatus: (id: number, title: string, isPinned: boolean) => 
          updatePinStatus(id, title, isPinned),
      };
    });
  }, [firmList, pinnedFirms, updatePinStatus]);

  if (isEmpty(firmList) && user?.is_superuser) {
    return <AddFirmAction onClickHandler={addFirmHandler} />;
  }

  if (!firmList || !firmListAuthorized || firmList.length === 0) {
    return (
      <FeatureEnabler enabler="createFirm" alternativeComponent={ContactOrInquireLicensing}>
        <>
          <AddFirmAction onClickHandler={addFirmHandler} />
          <LogoutButton />
        </>
      </FeatureEnabler>
    );
  }

  if (firmListAuthorized) {
    // Sort by pinned status (pinned items first)
    const sortedFirms = orderBy(processedFirmList, ['isPinned'], ['desc']);

    if (!searchState) {
      // if Search is empty return the full list;
      return (
        <>
          {sortedFirms.map((firm: LoginFirm) => (
            <FirmOption key={firm.id} {...firm} />
          ))}
        </>
      );
    }

    const filteredList = sortedFirms.filter((firm: LoginFirm) =>
      firm.name.toLowerCase().includes(searchState.toLowerCase())
    );

    if (filteredList.length === 0) {
      return <Typography variant="h4">FIRM NOT FOUND</Typography>;
    }

    return (
      <>
        {filteredList.map((firm: LoginFirm) => (
          <FirmOption key={firm.id} {...firm} />
        ))}
      </>
    );
  }

  return null;
};

export default FirmList;
